import * as React from 'react';
import { useContext, useState, useEffect } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import './RegistrationAuthenticateStep.css';
import { RootStoreContext } from '../../../stores/RootStore';
import { StepName } from 'src/config/Wizard';
import API from 'src/api/API';
import { FULL_PAGE } from 'src/components/SpinnerWrapper';
import { renderErrorMessageWithLink } from 'src/stores/ErrorMessages';
import { ErrorCode } from 'src/stores/RegistrationStore';
import NumberInputWithKeypad from './NumberInputWithKeypad';

const RegistrationAuthenticateStep = () => {
  const { registrationStore: { loadUser, setLoginPhoneNumber, setLoginEmail, savePendingLoginOtp },
          authStore: { isAdminFlow },
          wizardStore,
          spinnerStore,
          studioConfigStore
         } = useContext(RootStoreContext);
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>(isAdminFlow ? 'phone' : 'email');
  const [email, setEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState<boolean>(false);

  useEffect(() => {
    const fetchConfigs = async () => {
      if (!studioConfigStore.studioConfigs) {
        setIsLoadingConfigs(true);
        try {
          await studioConfigStore.fetchStudioConfigs();
        } catch (err) {
          console.error('Failed to fetch studio configs:', err);
        } finally {
          setIsLoadingConfigs(false);
        }
      }
    };

    fetchConfigs();
  }, [studioConfigStore]);

  // Show loading state while configs are being fetched
  if (isLoadingConfigs || !studioConfigStore.studioConfigs) {
    return (
      <Container className='mt-5'>
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  const handleLogin = async () => {
    const isPhoneLogin = loginMethod === 'phone';
    const loginTarget = isPhoneLogin ? phoneNumber.trim() : email.trim().toLowerCase();

    if (!loginTarget) {
      return;
    }

    if (isPhoneLogin) {
      setLoginPhoneNumber(loginTarget);
    } else {
      setLoginEmail(loginTarget);
    }
    studioConfigStore.resetSelectedDate();
    if (isAdminFlow) {
      loadUser(loginTarget);
    } else {
      try{
        spinnerStore.show(FULL_PAGE);
        const response = isPhoneLogin
          ? await API.post('/login/phone', { phoneNumber: loginTarget })
          : await API.post('/login/email', { email: loginTarget });
        const data = JSON.parse(response);
        
        const displayedLoginTarget = isPhoneLogin
          ? data.phoneNumber || loginTarget
          : data.email || loginTarget;

        savePendingLoginOtp(displayedLoginTarget, displayedLoginTarget, loginMethod);
        wizardStore.setStep(StepName.REGISTRATION_AUTHENTICATE_OTP);
      } catch (err: any) {
        setError(err?.error || 'An error occurred, please try again or contact our studio');
      }
      spinnerStore.hide(FULL_PAGE);
    }
  }

  const renderNumberInput = () => {
    return (
      <Row>
        <Col>
          <NumberInputWithKeypad
            value={phoneNumber}
            onChange={(value) => setPhoneNumber(value)}
            placeholder="Enter phone number to login"
            inputMode="tel"
            autoComplete="tel"
            onEnter={handleLogin}
          />
        </Col>
      </Row>
    );
  };

  const renderEmailInput = () => {
    return (
      <Row>
        <Col>
          <Form.Group controlId="login-email">
            <Form.Control
              className="login-email-input"
              value={email}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="Enter email to login"
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                  handleLogin();
                }
              }}
            />
          </Form.Group>
        </Col>
      </Row>
    );
  };

  const renderLoginError = () => {
    if (!error) {
      return null;
    }

    if (error === ErrorCode.THROTTLING_PHONE) {
      return renderErrorMessageWithLink(ErrorCode.THROTTLING_PHONE, studioConfigStore.studioHomepageStore).text;
    }

    if (error === ErrorCode.THROTTLING_EMAIL) {
      return 'Too many login code requests. Please wait a few minutes and try again.';
    }

    if (error === 'THROTTLING_LOGIN') {
      return 'Too many login attempts. Please wait a few minutes and try again.';
    }

    if (error === 'INVALID_EMAIL') {
      return 'Please enter a valid email address.';
    }

    return error;
  };

  const isPhoneLogin = loginMethod === 'phone';
  const loginTarget = isPhoneLogin ? phoneNumber.trim() : email.trim();

  return (
    <Container className='mt-5'>
      <div className="text-center mb-4">
        <h2 className="studio-name mb-3">{studioConfigStore.studioName}</h2>
        <p className="description mb-3">
          Get tickets to events and check-in to classes using your coins package.
        </p>
      </div>

      {error && (
        <Row className="mt-2 mb-2">
          <Col className="text-center">
            <div className="text-danger">{renderLoginError()}</div>
          </Col>
        </Row>
      )}
      <Row>
        <Col
          lg={{span: isPhoneLogin ? 4 : 5, offset: isPhoneLogin ? 4 : 3}}
          md={{span: isPhoneLogin ? 6 : 8, offset: isPhoneLogin ? 3 : 2}}
          sm={{span: 10, offset: 1}}
          xs={{span: 12, offset: 0}}
          className={`phone login-panel ${isPhoneLogin ? 'login-panel-phone' : 'login-panel-email'}`}
        >
          {isPhoneLogin ? renderNumberInput() : renderEmailInput()}
          <Row>
            <Col><Button className="btn-block login-primary-button" variant="info"
                         disabled={!loginTarget}
                         onClick={handleLogin}>
                     Login
                  </Button>
            </Col>
          </Row>
          {!isAdminFlow && (
            <>
              <Row>
                <Col className="login-divider-col">
                  <span className="login-divider-text">or</span>
                </Col>
              </Row>
              <Row>
                <Col className="login-alternative-col">
                  <Button
                    className="login-alternative-button"
                    variant="outline-secondary"
                    onClick={() => {
                      setError(null);
                      setLoginMethod(isPhoneLogin ? 'email' : 'phone');
                    }}
                  >
                    {isPhoneLogin ? 'Log in with email' : 'Log in with phone number'}
                  </Button>
                </Col>
              </Row>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default RegistrationAuthenticateStep;
